import { useCallback, useContext, useEffect, useRef, useState } from 'react'

import { reprocessMicroLearning } from '../../../api/api'
import { AppStateContext } from '../../../state/AppProvider'

const REPROCESS_SUCCESS_MESSAGE =
  'Micro-learning queued for reprocessing. Results will be available after the next pipeline run.'
const REPROCESS_ERROR_MESSAGE = 'Unable to reprocess the micro-learning. Please try again.'

export const useMicroLearningReprocess = (mlId: string, onChanged: () => void) => {
  const [isReprocessing, setIsReprocessing] = useState(false)
  const isReprocessingRef = useRef(false)
  const isMountedRef = useRef(true)
  const appStateContext = useContext(AppStateContext)
  const dispatchRef = useRef(appStateContext?.dispatch)
  dispatchRef.current = appStateContext?.dispatch

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const notify = useCallback((message: string, severity: 'success' | 'warning' | 'error') => {
    dispatchRef.current?.({
      type: 'SET_SNACKBAR_STATE',
      payload: { message, severity },
    })
  }, [])

  const reprocess = useCallback(async () => {
    if (isReprocessingRef.current) return false
    isReprocessingRef.current = true
    setIsReprocessing(true)

    try {
      const result = await reprocessMicroLearning(mlId)
      if (result.success) {
        // A failing refresh callback must not turn a successful reprocess into an error toast.
        try {
          onChanged()
        } catch (err) {
          console.error('Detail refresh failed after a successful reprocess.', err)
        }
        notify(result.message ?? REPROCESS_SUCCESS_MESSAGE, 'success')
        return true
      }

      notify(result.error ?? REPROCESS_ERROR_MESSAGE, 'error')
      return false
    } catch {
      notify(REPROCESS_ERROR_MESSAGE, 'error')
      return false
    } finally {
      isReprocessingRef.current = false
      if (isMountedRef.current) {
        setIsReprocessing(false)
      }
    }
  }, [mlId, notify, onChanged])

  return { isReprocessing, reprocess }
}
